/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { StorageWrapper } from './storage-wrapper';
import { ProductSections } from '../enums';

import type { VeeamRcopProductPrefixes, CompanyPrefixes } from '../../../enums';

export class AppStorage {

    protected readonly companyPrefix: CompanyPrefixes;
    protected readonly productPrefix: VeeamRcopProductPrefixes;
    protected readonly userPrefix: string;

    protected readonly localStorage: StorageWrapper;
    protected readonly sessionStorage: StorageWrapper;

    constructor(companyPrefix: CompanyPrefixes, productPrefix: VeeamRcopProductPrefixes, userPrefix: string) {
        this.companyPrefix = companyPrefix;
        this.productPrefix = productPrefix;
        this.userPrefix = userPrefix;

        this.localStorage = new StorageWrapper(localStorage);
        this.sessionStorage = new StorageWrapper(sessionStorage);
    }

    isLocalStorageSupported(): boolean {
        return this.localStorage.isSupported;
    }

    getItem(key: string): string | null {
        return this.getStorage().getItem(this.getFullPrefix(key));
    }

    getObjItem(key: string): Record<string, any> | null {
        const objItem: string | null = this.getItem(key);

        if (objItem) {
            try {
                return JSON.parse(objItem);
            } catch (err) {
                this.removeItem(key);
                err && console.error(err);
                return null;
            }
        }

        return null;
    }

    setItem(key: string, value: string | null): void {
        const storage: StorageWrapper = this.getStorage();

        try {
            storage.setItem(this.getFullPrefix(key), value);
        } catch (err) {
            if ((err as Error).name === 'QuotaExceededError') {
                this.clearKeysInStorage();
                storage.setItem(this.getFullPrefix(key), value);
            } else {
                err && console.error(err);
            }
        }
    }

    setObjItem(key: string, value: Record<string, any> | null): void {
        this.setItem(key, JSON.stringify(value));
    }

    removeItem(key: string): void {
        this.getStorage().removeItem(this.getFullPrefix(key));
    }

    getSessionItem(key: string): string | null {
        return this.sessionStorage.getItem(this.getFullPrefix(key));
    }

    getSessionObjItem(key: string): string | null {
        const objItem: string | null = this.getSessionItem(key);

        if (objItem) {
            try {
                return JSON.parse(objItem);
            } catch (err) {
                this.removeSessionItem(key);
                err && console.error(err);
                return null;
            }
        }

        return null;
    }

    setSessionItem(key: string, value: string | null): void {
        this.sessionStorage.setItem(this.getFullPrefix(key), value);
    }

    setSessionObjItem(key: string, value: Record<string, any> | null): void {
        this.setSessionItem(key, JSON.stringify(value));
    }

    removeSessionItem(key: string): void {
        this.sessionStorage.removeItem(this.getFullPrefix(key));
    }

    protected getFullPrefix(key: string): string {
        return `${this.getFullUserPrefix()}/${key}`;
    }

    protected getFullUserPrefix(): string {
        return `${this.getKeyProductPrefix()}/${this.userPrefix}`;
    }

    protected getStorage(): StorageWrapper {
        return this.localStorage.isSupported ? this.localStorage : this.sessionStorage;
    }

    protected clearKeysInStorage(): void {
        const excludeKeys: string[] = [
            `${this.companyPrefix}/${ProductSections.General}`,
            this.getKeyProductPrefix(),
        ];
        this.getStorage().removeMultipleItems('', excludeKeys);
    }

    protected getKeyProductPrefix(): string {
        return `${this.companyPrefix}/${this.productPrefix}`;
    }

}
